import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { countries } from "@/utils/countryList";
import { ageRangeOptions, educationLevelOptions } from "@/utils/selectOptions";
import { Skeleton } from "@/components/ui/skeleton";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface UserProfile {
  id: string;
  email: string;
  age_range: string | null;
  education_level: string | null;
  country: string | null;
}

const ProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [ageRange, setAgeRange] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [country, setCountry] = useState("");
  const isMounted = useRef(true);
  const fetchingRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    if (fetchingRef.current) {
      console.log("Fetch already in progress, skipping");
      return;
    }

    if (!user) {
      console.log("No user found, setting loading to false");
      if (isMounted.current) setLoading(false);
      return;
    }

    try {
      fetchingRef.current = true;
      if (isMounted.current) setLoading(true);
      
      console.log("Fetching profile for user ID:", user.id);
      console.log(`Running query: profiles.select('*').eq('id', '${user.id}').maybeSingle()`);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
        
      console.log("Raw Supabase response:", { data, error });
      
      if (error) {
        console.error('Error fetching profile:', error);
        if (isMounted.current) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to load your profile information.",
          });
        }
      } else if (data) {
        console.log("Profile data received:", data);
        if (isMounted.current) {
          setProfile(data);
          setAgeRange(data.age_range || "");
          setEducationLevel(data.education_level || "");
          setCountry(data.country || "");
        }
      } else {
        console.warn("No profile data found for user ID:", user.id);
        if (isMounted.current) {
          toast({
            variant: "destructive",
            title: "Profile Not Found",
            description: "Your profile information could not be found.",
          });
        }
      }
    } catch (error) {
      console.error('Error in try/catch block:', error);
      if (isMounted.current) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load your profile information.",
        });
      }
    } finally {
      console.log("Finally block reached, setting loading to false");
      if (isMounted.current) setLoading(false);
      fetchingRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    isMounted.current = true;
    
    console.log("Setting up profile fetch, user changed");
    
    if (user) {
      const timeoutId = setTimeout(() => {
        fetchProfile();
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      setLoading(false);
    }
    
    return () => {
      console.log("Cleanup function called, setting isMounted to false");
      isMounted.current = false;
    };
  }, [user, fetchProfile]);

  const handleUpdateProfile = async () => {
    if (!user) return;
    
    try {
      setUpdating(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          age_range: ageRange,
          education_level: educationLevel,
          country: country,
        })
        .eq('id', user.id);
        
      if (error) throw error;
      
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: "Failed to update your profile information.",
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mx-auto px-4 py-24">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
            <CardDescription>
              {!user 
                ? "Please sign in to view and update your profile." 
                : "Update your personal information and preferences."}
            </CardDescription>
          </CardHeader>
          {!user ? null : (
            loading ? (
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <Skeleton className="h-10 w-full" />
              </CardContent>
            ) : (
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={user?.email || ""} disabled />
                </div>
                
                <div>
                  <Label htmlFor="age-range">Age Range</Label>
                  <Select value={ageRange} onValueChange={setAgeRange}>
                    <SelectTrigger id="age-range">
                      <SelectValue placeholder="Select your age range" />
                    </SelectTrigger>
                    <SelectContent>
                      {ageRangeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="education-level">Education Level</Label>
                  <Select value={educationLevel} onValueChange={setEducationLevel}>
                    <SelectTrigger id="education-level">
                      <SelectValue placeholder="Select your education level" />
                    </SelectTrigger>
                    <SelectContent>
                      {educationLevelOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={handleUpdateProfile} 
                  disabled={updating}
                  className="w-full"
                >
                  {updating ? "Updating..." : "Update Profile"}
                </Button>
              </CardContent>
            )
          )}
        </Card>
      </div>
      <Footer />
    </>
  );
};

export default ProfilePage;
